import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { isAuthenticated } from '../api/auth';
import { fetchCardGroups } from '../api/learning-cards';
import CreateGroupModal from '../components/CreateGroupModal';
import GroupCard from '../components/GroupCard';
import { LEARNING_CARDS_ROUTES } from '../constants/learning-cards';

function GroupsPage() {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [authenticated, setAuthenticated] = useState(isAuthenticated());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [groups, setGroups] = useState([]);
    const [recentGroups, setRecentGroups] = useState([]);

    useEffect(() => {
        const authStatus = isAuthenticated();
        setAuthenticated(authStatus);

        if (authStatus) {
            loadGroups();
        } else {
            setLoading(false);
        }

        const handleStorageChange = () => {
            const newAuthStatus = isAuthenticated();
            setAuthenticated(newAuthStatus);

            if (newAuthStatus) {
                loadGroups();
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    const loadGroups = async () => {
        console.log('Loading groups...');
        setLoading(true);
        setError('');

        try {
            const result = await fetchCardGroups();
            console.log('Groups loaded:', result);

            if (result.success) {
                console.log('Setting groups state:', result.data.groups);
                const allGroups = result.data.groups || [];

                const sortedGroups = [...allGroups].sort((a, b) => {
                    return new Date(b.createdAt) - new Date(a.createdAt);
                });

                setGroups(sortedGroups);

                const recent = sortedGroups.slice(0, 3);
                setRecentGroups(recent);
            } else {
                setError(result.message || 'Failed to load flashcard groups');
            }
        } catch (err) {
            console.error('Load groups error:', err);
            setError('An unexpected error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleGroupCreated = () => {
        loadGroups();
    };

    if (!authenticated) {
        return <Navigate to={LEARNING_CARDS_ROUTES.BASE} replace />;
    }

    return (
        <div className="page-section">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>My Flashcard Groups</h2>
                <button
                    className="btn btn-primary"
                    onClick={() => setShowCreateModal(true)}
                >
                    Create New Group
                </button>
            </div>

            {error && (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="text-center my-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : (
                <>
                    {recentGroups.length > 0 && (
                        <div className="mb-5">
                            <h4 className="mb-3">Recently Used</h4>
                            <div className="row">
                                {recentGroups.map(group => (
                                    <div key={group.id} className="col-md-4 mb-3">
                                        <GroupCard
                                            group={group}
                                            cardCount={0}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mb-3">
                        <h4 className="mb-3">All Groups</h4>
                        {groups.length === 0 ? (
                            <div className="card">
                                <div className="card-body text-center py-5">
                                    <p className="mb-3">You don't have any flashcard groups yet.</p>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => setShowCreateModal(true)}
                                    >
                                        Create Your First Group
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="list-group">
                                {groups.map(group => (
                                    <GroupCard
                                        key={group.id}
                                        group={group}
                                        cardCount={0}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}

            <CreateGroupModal
                show={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onGroupCreated={handleGroupCreated}
            />
        </div>
    );
}

export default GroupsPage;